import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useMemo
} from "react";
import PropTypes from "kf-proptypes/widgets";
import { Trans } from "@lingui/macro";

import { useHideOnScroll } from "base/hooks";
import { i18n } from "base/i18n";

import { BaseDropdown, BASE_DROPDOWN_POSITION } from "./base.jsx";
import { Menu, MenuItem, MENU_SIZES } from "./menu.jsx";

import styles from "./dropdown.css";

export const FLOATING_MENU_POSITION = BASE_DROPDOWN_POSITION;

export const FLOATING_MENU_SIZES = MENU_SIZES;

/**
 * Floating Dropdown component can be used when user want to show anything in floating state
 */
export function FloatingMenuComponent(props, ref) {
  const {
    itemRenderer,
    className,
    targetClassName,
    dataSource,
    left,
    top,
    onSelect = () => {},
    onChange = () => {},
    getContainer = () => document.body,
    position = FLOATING_MENU_POSITION.BOTTOM,
    closeOnOutsideClick = false,
    hideOnScroll = true,
    footer,
    emptyRenderer,
    isGrouped,
    size,
    header,
    selection,
    selectable = false
  } = props;
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);

  useHideOnScroll({
    hideOnScroll,
    className: "ant-select-dropdown",
    setState: () => setIsOpen(false)
  });

  useImperativeHandle(ref, () => ({
    next: () => {
      if (currentIndex < dataSource.length - 1) {
        setCurrentIndex(currentIndex + 1);
        onChange(dataSource[currentIndex + 1], currentIndex + 1);
      } else {
        setCurrentIndex(0);
        onChange(dataSource[0], 0);
      }
    },
    previous: () => {
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
        onChange(dataSource[currentIndex - 1], currentIndex - 1);
      } else {
        setCurrentIndex(dataSource.length - 1);
        onChange(dataSource[dataSource.length - 1], currentIndex - 1);
      }
    },
    select: () => {
      onSelect(dataSource[currentIndex]);
    },
    show: () => {
      setIsOpen(true);
    },
    hide: () => {
      setIsOpen(false);
    }
  }));
  const defaultRenderer = (item, index) => {
    return <MenuItem key={index}>{item}</MenuItem>;
  };
  const defaultEmptyRenderer = () => {
    return (
      <Menu size={size}>
        <MenuItem disabled={true}>
          <Trans>No users found</Trans>
        </MenuItem>
      </Menu>
    );
  };
  const onClose = () => {
    setIsOpen(false);
  };

  const selectedKeys = useMemo(
    function selectedKeys() {
      return selection || currentIndex.toString();
    },
    [selection, currentIndex]
  );

  const defaultListRenderer = ({ items, itemRenderer }) => {
    const renderedItems =
      items && items.map((item, index) => itemRenderer(item, index));
    return (
      <>
        {header && header()}
        <div className={className}>
          <Menu
            selectable={selectable}
            selection={selectedKeys}
            onChange={(e) => onSelect(dataSource[e.key])}
            size={size}
          >
            {renderedItems}
          </Menu>
        </div>
        {footer && footer()}
      </>
    );
  };

  const groupedListRenderer = ({ items, itemRenderer }) => {
    return (
      <div className={`${className} ${styles.floatingMenuListRenderer}`}>
        {Object.keys(items).map((group) => (
          <div key={group}>
            <p className={`tiny-600 ${styles.floatingMenuGroup}`}>
              {i18n._(group)}
            </p>
            <Menu
              selectable={selectable}
              selection={selectedKeys}
              onChange={(e) => {
                const selectedItem = items[group].find(
                  (item) => String(item.value) === String(e.key)
                );
                onSelect(selectedItem);
              }}
            >
              {items[group].map((item, index) => itemRenderer(item, index))}
            </Menu>
          </div>
        ))}
      </div>
    );
  };

  return (
    <BaseDropdown
      dataSource={dataSource}
      itemRenderer={itemRenderer || defaultRenderer}
      isOpen={isOpen}
      closeOnOutsideClick={closeOnOutsideClick}
      position={position}
      getContainer={getContainer}
      onClose={onClose}
      hideOnScroll={hideOnScroll}
      listRenderer={({ items, itemRenderer }) => {
        return isGrouped
          ? groupedListRenderer({ items, itemRenderer })
          : defaultListRenderer({ items, itemRenderer });
      }}
      targetRenderer={() => {
        return (
          <div
            className={`${styles.dummytarget} ${targetClassName}`}
            style={{ top: top, left: left }}
          />
        );
      }}
      emptyRenderer={emptyRenderer || defaultEmptyRenderer}
    />
  );
}

FloatingMenuComponent.propTypes = {
  /**
   * User can customise the list items of dropdown by passing node to itemRenderer props
   */
  itemRenderer: PropTypes.func,
  /**
   * To overide the style of floating dropdown
   */
  className: PropTypes.string,
  /**
   * Data to be list in dropdown
   */
  dataSource: PropTypes.func,
  /**
   * To toggle grouped dropdown mode. Datasource must be changed accordingly
   */
  isGrouped: PropTypes.bool,
  /**
   * Determine at which dom element the dropdown should be placed
   */
  getContainer: PropTypes.func,
  /**
   * Position of the dropdown can be vary
   */
  position: PropTypes.string,
  /**
   * Left position where dropdown is shown be present
   */
  left: PropTypes.string,
  /**
   * top position where dropdown is shown be present
   */
  top: PropTypes.string,
  /**
   * Trigger when user click the dropdown
   */
  onChange: PropTypes.func,
  /**
   * Trigger when user selects the dropdown
   */
  onSelect: PropTypes.func,
  /**
   * To customise the footer of the dropdown
   */
  footer: PropTypes.func,
  /**
   * To customise the header of the dropdown
   */
  header: PropTypes.func,
  targetClassName: PropTypes.string,
  /**
   * Dropdown close if click outside the components
   */
  closeOnOutsideClick: PropTypes.bool,
  /**
   * Scrool behaviour to hide
   */
  hideOnScroll: PropTypes.bool,
  /**
   * If nothing there in datasource this empty render will get triggered
   */
  emptyRenderer: PropTypes.func,
  /**
   * Size of the dropdown can be vary bu this prop
   */
  size: PropTypes.string,
  /**
   * Default selected value
   */
  selection: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  /**
   * Props used to make user select the value
   */
  selectable: PropTypes.bool
};

FloatingMenuComponent.defaultProps = {
  onSelect: () => {},
  onChange: () => {},
  getContainer: () => document.body,
  position: FLOATING_MENU_POSITION.BOTTOM,
  closeOnOutsideClick: false,
  hideOnScroll: false
};

export const FloatingMenu = forwardRef(FloatingMenuComponent);
